import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';

import {
    PROVINCE,
    Typography,
    noticeError,
    BigNumberBox,
    PrimaryButton,
    ChecklistBlock,
    ProductCardLayout,
    PageBehaviourButton,
    BigNumberFormatValues,
    getDesignationMessage,
} from '@ratehub/base-ui';
import { DEPOSITS_VERTICALS, trackHeapEventForDepositsAndNavigate } from '@ratehub/inv-common';

import ChequingAccountPropType from '../definitions/ChequingAccountPropType';
import getTextForChequingFeature from '../functions/getTextForChequingFeature';
import ProductFeatureList from './ProductFeatureList';
import DepositsProductName from './DepositsProductName';
import ExpandableWithButton from './ExpandableWithButton';
import ChequingAccountFooter from './ChequingAccountFooter';
import ProductListChequingDetails from './ProductListChequingDetails';


function ProductCardLayoutChequing({
    chequingAccount,
    provinceCode,
    ...otherProps
}) {
    const productCard = getProductCardLayoutPropsForChequing(chequingAccount, provinceCode);

    return (
        <ProductCardLayout
            image={productCard.image}
            name={productCard.name}
            topRightElement={(
                <BigNumberBox
                    {...productCard.bigNumber}
                />
            )}

            details={productCard.details}
            rightOfDetailsElement={productCard.callToAction}

            footer={productCard.footer}
            designationMessage={productCard.designationMessage}
            messageBanner={productCard.messageBanner}
            analyticsObject={productCard.analyticsObject}
            {...otherProps}
        >
            {productCard.children}
        </ProductCardLayout>
    );
}

ProductCardLayoutChequing.propTypes = {
    chequingAccount: ChequingAccountPropType.isRequired,
    provinceCode: PropTypes.oneOf(Object.values(PROVINCE)),
};


function getProductCardLayoutPropsForChequing(chequingAccount, provinceCode) {
    return {
        messageBanner: chequingAccount.availableFeatures?.rewards,

        designationMessage: getDesignationMessage({
            isSponsored: chequingAccount.isSponsored,
            isMonetized: chequingAccount.isMonetized,
        }),

        name: {
            Component: (
                <DepositsProductName
                    name={chequingAccount.name}
                    provider={chequingAccount.provider}
                    isPFAWinner={chequingAccount.isPFAWinner}
                    onClick={
                        chequingAccount.isMonetized
                            ? () => trackHeapEventForDepositsAndNavigate({
                                product: chequingAccount,
                                shouldNavigate: true,
                                vertical: DEPOSITS_VERTICALS.CHEQUING,
                            })
                            : undefined
                    }
                />
            ),
        },
        bigNumber: getBigNumberMessages({
            monthlyCost: chequingAccount.monthlyCost,
            interest: chequingAccount.interest,
        }),

        details: <ProductListChequingDetails chequingAccount={chequingAccount} />,
        // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
        callToAction: chequingAccount.isMonetized
            ? (
                <PrimaryButton
                    size="small"
                    message={(
                        <FormattedMessage
                            id="web-components.chequing.cta"
                            defaultMessage="Open account"
                        />
                    )}
                    onClick={() => trackHeapEventForDepositsAndNavigate({
                        product: chequingAccount,
                        shouldNavigate: true,
                        provinceCode,
                        vertical: DEPOSITS_VERTICALS.CHEQUING,
                    })}
                    data-name="cc-product list-inquire-cta"
                />
            )
            : undefined,

        children: <MoreDetails product={chequingAccount} />,
        footer: (
            <ChequingAccountFooter
                transactions={chequingAccount.availableFeatures?.monthlyTransactions ?? 0}
                accountType={chequingAccount.type}
                eligibility={chequingAccount.eligibility}
            />
        ),

        analyticsObject: chequingAccount,
    };
}


function getBigNumberMessages({ monthlyCost, interest }) {
    return {
        message: (
            <FormattedMessage
                key="big-number-title"
                id="web-components.chequing.big-number.monthlyCost"
                defaultMessage="<bigger>{monthlyCost, number, CAD2}</bigger>"
                values={{
                    monthlyCost: monthlyCost,
                    ...BigNumberFormatValues,
                }}
            />
        ),

        subMessage: (
            <FormattedMessage
                key="big-number-sub-message"
                id="web-components.chequing.big-number.sub-message"
                defaultMessage="estimated monthly cost"
            />
        ),

        otherMessages: [
            {
                message: (
                    <FormattedMessage
                        key="big-number-interest-rate"
                        id="web-components.chequing.big-number.interest"
                        defaultMessage="<strong>{interest, number, percent2}</strong> interest rate"
                        values={{ interest: interest / 100 }}
                    />
                ),
            },
        ],
    };
}

// This "component" was extracted from the render function for simplification.
// It is not to be exported.
// eslint-disable-next-line react/no-multi-comp
function MoreDetails({ product }) {
    const intl = useIntl();

    const availableFeatures = createFeatures(intl, product);
    // to be appended to response markup and output via dangerouslySetInnerHTML
    const otherInfo = product.promotions ? cleanUpMarkup(product.promotions) : '';

    return (
        <>
            <ExpandableWithButton buttonDescriptor={LOCAL_MESSAGES.moreOrLess}>
                <h3 className={Typography.CLASSES.TITLE_2XS}>
                    <FormattedMessage
                        id="web-components.chequing.expandable-list-title"
                        defaultMessage="Other fees & available features"
                    />
                </h3>

                <If condition={product?.waysToBank?.length}>
                    <ChecklistBlock
                        features={getWaysToBank(product.waysToBank)}
                        className="rh-mb-1"
                    />
                </If>

                <If condition={availableFeatures}>
                    <ProductFeatureList
                        items={availableFeatures}
                        className="rh-mb-2"
                    />
                </If>

                <If condition={product.eligibility}>
                    <h3 className="rh-title-2xs">
                        <FormattedMessage
                            id="web-components.chequing.eligibility-title"
                            defaultMessage="Eligibility"
                        />
                    </h3>

                    <div
                        className="other-info rh-mb-2 eligibility"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: product.eligibility }}
                    />
                </If>

                <If condition={otherInfo || !product.isMonetized}>
                    <h3 className="rh-title-2xs">
                        <FormattedMessage
                            id="web-components.chequing.other-information-title"
                            defaultMessage="Other information"
                        />
                    </h3>

                    <div
                        className={classNames(
                            'other-info',
                            product.isMonetized
                                ? 'rh-mb-2'
                                : 'rh-mb-0',
                        )}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: otherInfo }}
                    />
                    {/* since we can’t insert the JSX PageBehaviourButton as a last item within the otherInfo markup string,
                        we will always append a <ul> with it */}
                    <If condition={!product.isMonetized}>
                        <ul className="rh-mb-2">
                            <li>
                                <PageBehaviourButton
                                    data-name="cta-click-chequing-learn-more"
                                    size="small"
                                    shouldLowerCaseMessage={false}
                                    onClick={() =>
                                        trackHeapEventForDepositsAndNavigate({
                                            product,
                                            vertical: DEPOSITS_VERTICALS.CHEQUING,
                                            shouldNavigate: true,
                                        })
                                    }
                                    message={(
                                        <FormattedMessage
                                            id="web-components.chequing-card.learn-more-at-site-snippet"
                                            defaultMessage="Learn more on provider site"
                                        />
                                    )}
                                />
                            </li>
                        </ul>
                    </If>
                </If>
            </ExpandableWithButton>
        </>
    );
}

MoreDetails.propTypes = {
    product: PropTypes.shape({
        promotions: PropTypes.string,
        isMonetized: PropTypes.bool,
        // Mobx array
        waysToBank: PropTypes.oneOfType([
            PropTypes.array,
            PropTypes.object,
        ]),
        applyURL: PropTypes.string,
        eligibility: PropTypes.string,
    }).isRequired,
};

function createFeatures(intl, chequingAccount) {
    if (!chequingAccount.availableFeatures) {
        return undefined;
    }

    // traverse the provided availableFeatures and output their corresponding text
    return Object.keys(chequingAccount.availableFeatures)
        .map(feature => {
            const message = FEATURE_TO_MESSAGE_MAP[feature]?.message;
            if (!message) {
                noticeError(new RangeError(`[ProductListChequing] unrecognized feature: “${feature}”`));
                return undefined;
            }

            let value = chequingAccount.availableFeatures[feature];
            // generally, value should not be null, except for these where null implies 0
            if ((value === null || value === undefined) && NULL_MEANS_0_FEATURES.includes(feature)) {
                value = 0;
            }

            const isDollars = FEATURE_TO_MESSAGE_MAP[feature]?.isDollars;
            const isPercentage = FEATURE_TO_MESSAGE_MAP[feature]?.isPercentage;

            // generate an array of term/definition pairs for each feature specified
            return value !== null && value !== undefined
                ? {
                    term: <FormattedMessage {...message} />,
                    definition: (
                        <strong>
                            {getTextForChequingFeature({ intl, value, isDollars, isPercentage })}
                        </strong>
                    ),
                }
                : undefined;
        })
        .filter(featureText => featureText);
}

function getWaysToBank(waysToBank) {
    return waysToBank.map(key => LOCAL_MESSAGES[key]).filter(ways => ways);
}

function cleanUpMarkup(otherInfo) {
    return otherInfo
        .replace(/h[123]>/gi, 'h4>')
        .replace(/<p>&nbsp;<\/p>/gi, '');
}

const LOCAL_MESSAGES = defineMessages({
    moreOrLess: {
        id: 'web-components.chequing-card.show-more',
        defaultMessage: '{isExpanded, select, true {less details} other {more details}}',
    },

    // chequing features
    monthlyTransactions: {
        id: 'web-components.chequing.monthly-transactions',
        defaultMessage: 'Number of monthly transactions',
    },
    selfServeTransactions: {
        id: 'web-components.chequing.self-serve-transactions',
        defaultMessage: 'Number of self-serve transactions',
    },
    tellerAssistedTransactions: {
        id: 'web-components.chequing.teller-assisted-transactions',
        defaultMessage: 'Number of teller-assisted transactions',
    },
    chequeTransactions: {
        id: 'web-components.chequing.cheque-transactions',
        defaultMessage: 'Number of cheque transactions',
    },

    interacETransfers: {
        id: 'web-components.chequing.interac-e-transfers',
        defaultMessage: 'Number of Interac e-transfers',
    },

    bankDrafts: {
        id: 'web-components.chequing.bank-drafts',
        defaultMessage: 'Number of bank drafts',
    },

    bankDraftCost: {
        id: 'web-components.chequing.bank-draft-cost',
        defaultMessage: 'Bank draft cost',
    },

    travellersCheques: {
        id: 'web-components.chequing.travellers-cheques',
        defaultMessage: 'Travellers cheques',
    },

    safetyDepositRebate: {
        id: 'web-components.chequing.safety-deposit-rebate',
        defaultMessage: 'Safety deposit rebate',
    },

    creditCardRebate: {
        id: 'web-components.chequing.credit-card-rebate',
        defaultMessage: 'Credit card rebate',
    },

    additionalAccounts: {
        id: 'web-components.chequing.additional-accounts',
        defaultMessage: 'Additional accounts',
    },

    personalizedCheques: {
        id: 'web-components.chequing.personalized-cheques',
        defaultMessage: 'Personalized cheques',
    },

    rewards: {
        id: 'web-components.chequing.rewards',
        defaultMessage: 'Rewards',
    },

    overdraftFee: {
        id: 'web-components.chequing.overdraft-fee',
        defaultMessage: 'Overdraft fee',
    },

    overdraftInterest: {
        id: 'web-components.chequing.overdraft-interest',
        defaultMessage: 'Overdraft interest',
    },

    paperlessStatement: {
        id: 'web-components.chequing.paperless-statement',
        defaultMessage: 'Paperless statement',
    },

    mailedMonthlyStatement: {
        id: 'web-components.chequing.mailed-monthly-statement',
        defaultMessage: 'Mailed monthly statement',
    },

    // the following five keys must match array values within the API response shape for "ways to bank"
    mobileApp: {
        id: 'web-components.chequing.ways.mobile-app',
        defaultMessage: 'Mobile app',
    },

    inBranch: {
        id: 'web-components.chequing.ways.in-branch',
        defaultMessage: 'In-branch banking',
    },

    onlineBanking: {
        id: 'web-components.chequing.ways.online',
        defaultMessage: 'Online banking',
    },

    ABM: {
        id: 'web-components.chequing.ways.abm',
        defaultMessage: 'ABM network',
    },

    telephoneBanking: {
        id: 'web-components.chequing.ways.telephone',
        defaultMessage: 'Telephone banking',
    },
});

const FEATURE_TO_MESSAGE_MAP = {
    monthlyTransactions: {
        message: LOCAL_MESSAGES.monthlyTransactions,
    },
    selfServeTransactions: {
        message: LOCAL_MESSAGES.selfServeTransactions,
    },
    tellerAssistedTransactions: {
        message: LOCAL_MESSAGES.tellerAssistedTransactions,
    },
    chequeTransactions: {
        message: LOCAL_MESSAGES.chequeTransactions,
    },
    interacETransfers: {
        message: LOCAL_MESSAGES.interacETransfers,
    },
    bankDrafts: {
        message: LOCAL_MESSAGES.bankDrafts,
    },
    bankDraftCost: {
        message: LOCAL_MESSAGES.bankDraftCost,
        isDollars: true,
    },
    travellersCheques: {
        message: LOCAL_MESSAGES.travellersCheques,
    },
    safetyDepositRebate: {
        message: LOCAL_MESSAGES.safetyDepositRebate,
        isDollars: true,
    },
    creditCardRebate: {
        message: LOCAL_MESSAGES.creditCardRebate,
        isDollars: true,
    },
    additionalAccounts: {
        message: LOCAL_MESSAGES.additionalAccounts,
    },
    personalizedCheques: {
        message: LOCAL_MESSAGES.personalizedCheques,
    },

    rewards: {
        message: LOCAL_MESSAGES.rewards,
    },
    overdraftFee: {
        message: LOCAL_MESSAGES.overdraftFee,
        isDollars: true,
    },
    overdraftInterest: {
        message: LOCAL_MESSAGES.overdraftInterest,
        isPercentage: true,
    },
    paperlessStatement: {
        message: LOCAL_MESSAGES.paperlessStatement,
    },
    mailedMonthlyStatement: {
        message: LOCAL_MESSAGES.mailedMonthlyStatement,
        isDollars: true,
    },
};

const NULL_MEANS_0_FEATURES = [
    'number_of_monthly_transactions',
    'number_of_self_serve_transactions',
    'number_of_teller_assisted_transactions',
    'number_of_cheque_transactions',
    'number_of_interac_e_transfers',
    'number_of_bank_drafts',
];


export default ProductCardLayoutChequing;
